import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Box,
  Card,
  CardContent,
  IconButton,
  Divider,
  TextField,
  Alert,
  Fade,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  WhatsApp,
  Schedule,
  Send,
  Business,
  ArrowDropDown,
} from "@mui/icons-material";
import { useSEO } from "../../hooks/useSEO";
import { locations } from "../../constants";
import { CONTACT_INFO } from "../../constants/contactInfo";
import CopyButton from "../../components/common/CopyButton";

const ContactPage = () => {
  // SEO para página de contacto
  useSEO({
    title: "Contacto",
    description:
      "Ponte en contacto con Venta Usados Tattersall. Encuentra nuestros teléfonos, correos electrónicos, ubicaciones y horarios de atención. Estamos aquí para ayudarte.",
    keywords:
      "contacto tattersall, teléfono ventas usados, ubicación tattersall, horarios atención, contacto venta vehículos",
    image: "/og-contact.jpg",
    type: "website",
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [whatsappAnchor, setWhatsappAnchor] = useState(null);

  // Usar información de contacto centralizada
  const contactInfo = {
    phones: CONTACT_INFO.phones,
    emails: CONTACT_INFO.emails,
    addresses: locations,
    schedule: {
      weekdays: CONTACT_INFO.schedule.weekdays,
      saturday: CONTACT_INFO.schedule.friday,
      sunday: CONTACT_INFO.schedule.weekend,
    },
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Aquí manejarías el envío del formulario
    console.log("Form submitted:", formData);
  };

  const handleWhatsAppMenuOpen = (event) => {
    setWhatsappAnchor(event.currentTarget);
  };

  const handleWhatsAppMenuClose = () => {
    setWhatsappAnchor(null);
  };

  const handleWhatsAppClick = (phone) => {
    const url = `https://wa.me/${phone.number.replace(/[\s+]/g, "")}`;
    window.open(url, "_blank");
    handleWhatsAppMenuClose();
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
          Contáctanos
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: "auto" }}
        >
          Estamos aquí para ayudarte. Ponte en contacto con nosotros a través de
          cualquiera de estos medios.
        </Typography>
      </Box>

      <Grid
        container
        spacing={4}
        sx={{ display: "flex", justifyContent: "center" }}
      >
        {/* Información de Contacto */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Grid container spacing={3}>
            {/* Teléfonos */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Phone sx={{ color: "primary.main", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Teléfonos
                    </Typography>
                  </Box>

                  {contactInfo.phones.map((phone, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {phone.label}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<Phone />}
                          href={`tel:${phone.number.replace(/\s/g, "")}`}
                          sx={{ flexGrow: 1 }}
                        >
                          {phone.number}
                        </Button>
                        <CopyButton
                          text={phone.number}
                          id={`phone-${index}`}
                          showFeedback={false}
                        />
                      </Box>
                    </Box>
                  ))}

                  <Divider sx={{ my: 2 }} />

                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<WhatsApp />}
                    endIcon={<ArrowDropDown />}
                    onClick={handleWhatsAppMenuOpen}
                    fullWidth
                    sx={{
                      backgroundColor: "#25D366",
                      "&:hover": { backgroundColor: "#128C7E" },
                    }}
                  >
                    Contactar por WhatsApp
                  </Button>

                  <Menu
                    anchorEl={whatsappAnchor}
                    disableScrollLock
                    open={Boolean(whatsappAnchor)}
                    onClose={handleWhatsAppMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    {contactInfo.phones
                      .filter((phone) => phone.whatsapp)
                      .map((phone, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => handleWhatsAppClick(phone)}
                        >
                          <ListItemIcon>
                            <WhatsApp
                              fontSize="small"
                              sx={{ color: "#25D366" }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={phone.label}
                            secondary={phone.number}
                          />
                        </MenuItem>
                      ))}
                  </Menu>
                </CardContent>
              </Card>
            </Grid>

            {/* Emails */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Email sx={{ color: "primary.main", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Correos Electrónicos
                    </Typography>
                  </Box>

                  {contactInfo.emails.map((emailInfo, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {emailInfo.label}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<Email />}
                          href={`mailto:${emailInfo.email}`}
                          sx={{ flexGrow: 1 }}
                        >
                          {emailInfo.email}
                        </Button>
                        <CopyButton
                          text={emailInfo.email}
                          id={`email-${index}`}
                          showFeedback={false}
                        />
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Ubicaciones */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <LocationOn sx={{ color: "primary.main", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Nuestras Ubicaciones
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {contactInfo.addresses.map((location, index) => (
                      <Grid size={{ xs: 12, md: 6 }} key={index}>
                        <Paper
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            <Business
                              sx={{
                                fontSize: 16,
                                mr: 0.5,
                                verticalAlign: "middle",
                              }}
                            />
                            {location.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            {location.address}
                          </Typography>

                          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LocationOn />}
                              href={`${location.googleRef}`}
                              target="_blank"
                            >
                              Ver en Google Maps
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              href={`${location.wazeRef}`}
                              startIcon={<LocationOn />}
                              target="_blank"
                            >
                              Abrir en Waze
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Horarios */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Schedule sx={{ color: "primary.main", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Horarios de Atención
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {contactInfo.schedule.weekdays}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {contactInfo.schedule.saturday}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        color="text.secondary"
                      >
                        {contactInfo.schedule.sunday}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Formulario de Contacto */}
        {/* <Grid size={{ xs: 12, sm: 12, md: 12, lg: 4 }}>
          <Paper sx={{ p: 3, position: "sticky", top: 100 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Envíanos un Mensaje
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              ¿Tienes alguna consulta específica? Escríbenos y te responderemos
              a la brevedad.
            </Typography>

            <form onSubmit={handleFormSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Nombre completo"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  fullWidth
                />

                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  fullWidth
                />

                <TextField
                  label="Teléfono"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  fullWidth
                />

                <TextField
                  label="Mensaje"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  required
                  fullWidth
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<Send />}
                  fullWidth
                >
                  Enviar Mensaje
                </Button>
              </Box>
            </form>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                También puedes llamarnos directamente al{" "}
                <strong>{contactInfo.phones[0].number} </strong>
                para una atención más rápida.
              </Typography>
            </Alert>
          </Paper>
        </Grid> */}
      </Grid>
    </Container>
  );
};

export default ContactPage;
